import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  FaSeedling, 
  FaDownload, 
  FaSearch, 
  FaCalendarAlt, 
  FaShoppingCart,
  FaUtensils,
  FaCloud,
  FaStar,
  FaLeaf,
  FaMobileAlt,
  FaChevronDown,
  FaPalette,
  FaUsers,
  FaImage
} from 'react-icons/fa';
import JSZip from 'jszip';
// Import assets
import seasonLogo from '../assets/season512.jpg';
import mainSplash from '../assets/main-splash.jpg';
import recettesImg from '../assets/Recettes.jpg';
import courseImg from '../assets/course.jpg';
import cuisineImg from '../assets/cuisine.jpg';
import saisonImg from '../assets/saison.jpg';
import planificateurImg from '../assets/planificateur.jpg';

interface AssetItem {
  id: string;
  name: string;
  src: string;
  alt: string;
}

function Media() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const assets: AssetItem[] = [
    { id: 'logo', name: 'Logo', src: seasonLogo, alt: 'Logo Season' },
    { id: 'main-splash', name: 'Main Splash', src: mainSplash, alt: 'Image principale' },
    { id: 'recettes', name: 'Recettes', src: recettesImg, alt: 'Image recettes' },
    { id: 'course', name: 'Courses', src: courseImg, alt: 'Image courses' },
    { id: 'cuisine', name: 'Cuisine', src: cuisineImg, alt: 'Image cuisine' },
    { id: 'saison', name: 'Saison', src: saisonImg, alt: 'Image saison' },
    { id: 'planificateur', name: 'Planificateur', src: planificateurImg, alt: 'Image planificateur' },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleToggleSection = (sectionId: string) => {
    const isCurrentlyOpen = expandedSection === sectionId;
    const newSectionId = isCurrentlyOpen ? null : sectionId;
    
    setExpandedSection(newSectionId);
    
    // Scroll vers la section si on l'ouvre
    if (!isCurrentlyOpen && sectionRefs.current[sectionId]) {
      setTimeout(() => {
        const sectionElement = sectionRefs.current[sectionId];
        if (sectionElement) {
          // Calculer la position de l'élément
          const elementTop = sectionElement.getBoundingClientRect().top;
          const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
          
          // Trouver la navbar et calculer sa hauteur
          const navbar = document.querySelector('nav');
          const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 60;
          
          // Calculer la position finale avec un petit offset supplémentaire
          const offset = navbarHeight + 20; // 20px d'espace supplémentaire
          const targetPosition = elementTop + scrollPosition - offset;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, 100); // Petit délai pour que le contenu s'affiche
    }
  };

  const handleAssetToggle = (assetId: string) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const handleToggleAll = () => {
    if (selectedAssets.size === assets.length) {
      // Tout désélectionner
      setSelectedAssets(new Set());
    } else {
      // Tout sélectionner
      setSelectedAssets(new Set(assets.map(a => a.id)));
    }
  };

  const allSelected = selectedAssets.size === assets.length;

  const handleDownloadZip = async () => {
    if (selectedAssets.size === 0) {
      return;
    }

    try {
      const zip = new JSZip();
      
      // Wait for all fetches to complete and add to zip
      await Promise.all(
        Array.from(selectedAssets).map(async (assetId) => {
          const asset = assets.find(a => a.id === assetId);
          if (!asset) return;
          
          const response = await fetch(asset.src);
          const blob = await response.blob();
          const extension = asset.src.split('.').pop() || 'jpg';
          const filename = `${asset.name}.${extension}`;
          
          zip.file(filename, blob);
        })
      );

      // Generate and download the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'season-assets.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du ZIP:', error);
      alert('Une erreur est survenue lors du téléchargement.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Season: Listes et Recettes - Fiche technique - médias</title>
        <meta
          name="description"
          content="Découvrez Season, l'application mobile innovante pour cuisiner de manière responsable avec les produits de saison. Base de données complète, importation de recettes, planificateur de repas et listes de courses intelligentes."
        />
        <meta
          name="keywords"
          content="Season app, application cuisine, produits de saison, recettes, planification repas, liste de courses, cuisine responsable, France, Android"
        />
        <meta property="og:title" content="Season: Listes et Recettes - Fiche technique pour les médias" />
        <meta
          property="og:description"
          content="Découvrez Season, l'application mobile innovante pour cuisiner de manière responsable avec les produits de saison."
        />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="max-w-5xl mx-auto px-3 md:px-8 py-4 md:py-12 space-y-6 md:space-y-8">
        <header className="space-y-4">
          <h1 className="md:text-4xl text-3xl font-bold text-white">
            Season: Listes et Recettes
          </h1>
          <p className="text-lg text-gray-300 md:text-xl">
            Fiche technique - Médias
          </p>
        </header>

        {/* Présentation générale */}
        <section className="space-y-4">
          <h2 className="md:text-2xl text-xl font-semibold text-white">
            Présentation générale
          </h2>
          <div className="bg-neutral-800 rounded-lg p-4 md:p-6 border border-gray-700">
            <p className="text-gray-50 leading-relaxed">
              <strong>Season</strong> est une application mobile innovante conçue pour vous aider à cuisiner de manière plus responsable et organisée en privilégiant les produits de saison. Disponible sur Android, l'application combine gestion de recettes, planification de repas, listes de courses intelligentes et une base de données complète de produits de saison pour la France.
            </p>
          </div>
        </section>

        {/* Base de données de produits de saison */}
        <div 
          ref={(el) => (sectionRefs.current["produits-saison"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("produits-saison")}
          >
            <div className="flex items-center gap-3">
              <FaSeedling className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Base de données de produits de saison
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "produits-saison" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "produits-saison" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                L'application dispose d'une <strong>base de données exhaustive</strong> contenant des centaines de produits locaux français avec leurs informations saisonnières :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Catégories incluses</strong> : Légumes, fruits, poissons, fruits de mer</li>
                <li><strong>Informations détaillées</strong> pour chaque produit :
                  <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                    <li>Disponibilité mensuelle (janvier à décembre)</li>
                    <li>Période de conservation optimale</li>
                    <li>Conseils de conservation</li>
                    <li>Informations nutritionnelles (calories, empreinte carbone)</li>
                    <li>Images illustratives</li>
                  </ul>
                </li>
                <li><strong>Recherche avancée</strong> : Recherche par nom, type, catégorie ou période de l'année</li>
                <li><strong>Consultation en temps réel</strong> : Voir quels produits sont de saison à tout moment de l'année</li>
              </ul>
              <p className="mt-4">
                Cette base de données vous permet de découvrir facilement quels produits sont disponibles localement selon la période, encourageant une consommation plus respectueuse de l'environnement et supportant les producteurs locaux.
              </p>
            </div>
          )}
        </div>

        {/* Importation de recettes intelligente */}
        <div 
          ref={(el) => (sectionRefs.current["importation"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("importation")}
          >
            <div className="flex items-center gap-3">
              <FaDownload className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Importation de recettes intelligente
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "importation" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "importation" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                Season offre <strong>plusieurs méthodes d'importation</strong> de recettes depuis Internet :
              </p>
              
              <div className="space-y-4 mt-4">
                <div>
                  <h4 className="font-semibold mb-2">Importation depuis une URL web</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Extraction automatique</strong> des données structurées (format Schema.org)</li>
                    <li><strong>Importation complète</strong> incluant :
                      <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                        <li>Titre, description, image</li>
                        <li>Liste des ingrédients avec quantités</li>
                        <li>Instructions étape par étape</li>
                        <li>Temps de préparation et cuisson</li>
                        <li>Nombre de portions</li>
                        <li>Auteur et source originale</li>
                      </ul>
                    </li>
                    <li><strong>Traitement intelligent</strong> : L'application reconnaît et structure automatiquement les informations, même si certains éléments sont manquants</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Recherche Google intégrée</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Recherche directement dans l'application</strong> : Trouvez des recettes en ligne sans quitter Season</li>
                    <li><strong>Importation en un clic</strong> depuis les résultats de recherche</li>
                    <li><strong>Prise en charge de nombreux sites de recettes</strong> populaires</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Gestion des recettes importées</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Conservation du lien source</strong> : Retrouvez facilement la recette originale</li>
                    <li><strong>Édition possible</strong> : Modifiez et personnalisez les recettes importées</li>
                    <li><strong>Vérification automatique</strong> : L'application détecte si des ingrédients ou instructions sont manquants</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Détection automatique des produits de saison */}
        <div 
          ref={(el) => (sectionRefs.current["detection"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("detection")}
          >
            <div className="flex items-center gap-3">
              <FaSearch className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Détection automatique des produits de saison
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "detection" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "detection" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                Une fonctionnalité unique de Season est la <strong>détection automatique</strong> des ingrédients de saison dans vos recettes :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Analyse en temps réel</strong> : Lors de la consultation ou création d'une recette, l'application analyse automatiquement chaque ingrédient</li>
                <li><strong>Identification des produits de saison</strong> : Compare les ingrédients avec la base de données pour déterminer lesquels sont de saison au moment actuel</li>
                <li><strong>Indicateur visuel</strong> : Visualisez facilement combien d'ingrédients de votre recette sont de saison</li>
                <li><strong>Recommandations</strong> : Suggestions pour adapter vos recettes selon les saisons</li>
                <li><strong>Normalisation intelligente</strong> : L'application reconnaît les variations de noms (singulier/pluriel, différentes orthographes) pour une détection précise</li>
              </ul>
              <p className="mb-4">
                Cette fonctionnalité vous aide à :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Faire des choix plus écoresponsables</li>
                <li>Optimiser le coût de vos courses (les produits de saison sont généralement moins chers)</li>
                <li>Profiter de produits à leur meilleur goût</li>
                <li>Réduire votre empreinte carbone</li>
              </ul>
            </div>
          )}
        </div>

        {/* Planificateur de repas hebdomadaire */}
        <div 
          ref={(el) => (sectionRefs.current["planificateur"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("planificateur")}
          >
            <div className="flex items-center gap-3">
              <FaCalendarAlt className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Planificateur de repas hebdomadaire
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "planificateur" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "planificateur" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                Organisez facilement vos repas de la semaine avec le <strong>planificateur de repas intuitif</strong> :
              </p>

              <div className="space-y-4 mt-4">
                <div>
                  <h4 className="font-semibold mb-2">Fonctionnalités principales</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Vue hebdomadaire</strong> : Visualisez et planifiez vos repas du lundi au dimanche</li>
                    <li><strong>Navigation temporelle</strong> : Naviguez facilement entre les semaines précédentes et futures</li>
                    <li><strong>Sélection de date</strong> : Sélectionnez une date spécifique depuis un calendrier pour accéder directement à la semaine correspondante</li>
                    <li><strong>Types de repas</strong> : Catégorisez vos repas (petit-déjeuner, déjeuner, dîner, collation, autres)</li>
                    <li><strong>Affichage visuel</strong> : Chaque recette planifiée affiche :
                      <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                        <li>Image de la recette</li>
                        <li>Titre</li>
                        <li>Badge du type de repas (avec code couleur)</li>
                        <li>Accès rapide aux détails de la recette</li>
                      </ul>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Gestion avancée des repas planifiés</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Menu contextuel</strong> : Appuyez longuement sur un repas pour accéder à des options :
                      <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                        <li><strong>Changer le type de repas</strong> : Transformez un déjeuner en dîner</li>
                        <li><strong>Changer la date</strong> : Déplacez un repas à une autre date (avec navigation automatique vers la nouvelle semaine si nécessaire)</li>
                        <li><strong>Dupliquer le repas</strong> : Créez une copie du repas pour une autre date sans supprimer l'original</li>
                        <li><strong>Supprimer</strong> : Retirez un repas du planning avec confirmation</li>
                      </ul>
                    </li>
                    <li><strong>Duplication intelligente</strong> : Les repas dupliqués conservent le lien avec la recette originale tout en étant indépendants</li>
                    <li><strong>Interface en français</strong> : Toutes les dates et jours de la semaine sont affichés en français</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Intégration avec les recettes</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Sélection depuis votre collection</strong> : Choisissez parmi toutes vos recettes sauvegardées</li>
                    <li><strong>Filtrage et recherche</strong> : Trouvez rapidement la recette idéale pour chaque repas</li>
                    <li><strong>Pas de limite</strong> : Planifiez autant de repas que vous souhaitez par jour</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Liste de courses intelligente */}
        <div 
          ref={(el) => (sectionRefs.current["liste-courses"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("liste-courses")}
          >
            <div className="flex items-center gap-3">
              <FaShoppingCart className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Liste de courses intelligente
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "liste-courses" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "liste-courses" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                Générez et gérez vos listes de courses de manière efficace :
              </p>

              <div className="space-y-4 mt-4">
                <div>
                  <h4 className="font-semibold mb-2">Génération automatique</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Depuis le planificateur</strong> : Créez automatiquement une liste de courses à partir des repas planifiés</li>
                    <li><strong>Agrégation intelligente</strong> : L'application combine automatiquement les mêmes ingrédients de différentes recettes</li>
                    <li><strong>Calcul des quantités</strong> : Additionne les quantités nécessaires pour tous vos repas</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Gestion complète des listes</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Recherche de produits</strong> : Accédez à une base de produits étendue pour ajouter des articles</li>
                    <li><strong>Produits personnalisés</strong> : Ajoutez vos propres produits avec quantités, unités et notes</li>
                    <li><strong>Organisation par sections</strong> : Organisez votre liste selon les rayons du magasin (légumes, viandes, épicerie, etc.)</li>
                    <li><strong>Notes personnelles</strong> : Ajoutez des notes spécifiques à chaque produit</li>
                    <li><strong>Historique</strong> : Accédez rapidement aux produits que vous avez utilisés précédemment</li>
                    <li><strong>Cochez au fur et à mesure</strong> : Marquez les articles achetés pendant vos courses</li>
                    <li><strong>Listes sauvegardées</strong> : Créez et sauvegardez plusieurs listes (par semaine, événement, etc.)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Expérience utilisateur optimisée</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Interface intuitive</strong> : Ajout et suppression rapides</li>
                    <li><strong>Images des produits</strong> : Visualisation claire de chaque article</li>
                    <li><strong>Notifications</strong> : Rappels optionnels pour vos courses</li>
                    <li><strong>Partage</strong> : Partagez vos listes avec votre famille</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Création et édition de recettes */}
        <div 
          ref={(el) => (sectionRefs.current["creation-recettes"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("creation-recettes")}
          >
            <div className="flex items-center gap-3">
              <FaUtensils className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Création et édition de recettes
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "creation-recettes" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "creation-recettes" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                Créez vos propres recettes ou personnalisez celles importées :
              </p>

              <div className="space-y-4 mt-4">
                <div>
                  <h4 className="font-semibold mb-2">Création de recettes</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Formulaire complet</strong> :
                      <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                        <li>Titre et description</li>
                        <li>Image (prise de photo ou galerie)</li>
                        <li>Nombre de portions</li>
                        <li>Temps de préparation et cuisson</li>
                        <li>Niveau de difficulté</li>
                        <li>Tags et catégories</li>
                        <li>Auteur et source (si applicable)</li>
                      </ul>
                    </li>
                    <li><strong>Gestion des ingrédients</strong> :
                      <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                        <li>Ajout depuis la base de données de produits</li>
                        <li>Quantités et unités personnalisables</li>
                        <li>Notes par ingrédient</li>
                        <li>Détection automatique des produits de saison</li>
                      </ul>
                    </li>
                    <li><strong>Instructions étape par étape</strong> : Ajoutez autant d'étapes que nécessaire</li>
                    <li><strong>Informations complémentaires</strong> : Notes, astuces, conseils de conservation</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Édition de recettes</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Modification complète</strong> : Modifiez tous les aspects d'une recette existante</li>
                    <li><strong>Préservation des données</strong> : Les dates de création sont conservées, la date de modification est mise à jour automatiquement</li>
                    <li><strong>Duplication</strong> : Créez des variantes d'une recette existante</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sauvegarde et synchronisation */}
        <div 
          ref={(el) => (sectionRefs.current["sauvegarde"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("sauvegarde")}
          >
            <div className="flex items-center gap-3">
              <FaCloud className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Sauvegarde et synchronisation
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "sauvegarde" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "sauvegarde" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <div className="space-y-4 mt-4">
                <div>
                  <h4 className="font-semibold mb-2">Sauvegarde locale</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Stockage sécurisé</strong> : Toutes vos données sont stockées localement sur votre appareil</li>
                    <li><strong>Backup automatique</strong> : Sauvegarde automatique de vos recettes et planifications</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Export/Import de données (format .seas)</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Export complet</strong> : Exportez toutes vos données (recettes, listes, planifications)</li>
                    <li><strong>Format propriétaire .seas</strong> : Format optimisé pour préserver toutes les informations</li>
                    <li><strong>Import</strong> : Restaurez vos données depuis un fichier .seas</li>
                    <li><strong>Partage</strong> : Partagez vos données entre appareils ou avec d'autres utilisateurs</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Synchronisation Google Drive (Premium)</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Sauvegarde dans le cloud</strong> : Connectez-vous avec votre compte Google</li>
                    <li><strong>Synchronisation automatique</strong> : Vos données sont sauvegardées automatiquement sur Google Drive</li>
                    <li><strong>Multi-appareils</strong> : Accédez à vos données depuis n'importe quel appareil</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modèle économique */}
        <div 
          ref={(el) => (sectionRefs.current["premium"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("premium")}
          >
            <div className="flex items-center gap-3">
              <FaStar className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Modèle économique
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "premium" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "premium" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                Season fonctionne selon un <strong>modèle freemium</strong>, offrant une version gratuite complète avec publicités et une version Premium sans publicités :
              </p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Version gratuite</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Bannière publicitaire non intrusive</strong> : Une bannière discrète s'affiche dans l'application sans perturber l'expérience utilisateur</li>
                    <li><strong>Publicité plein écran</strong> : Une publicité plein écran apparaît uniquement lors de l'importation de recettes depuis le web, et seulement toutes les <strong>10 minutes maximum</strong>. Cette limitation garantit une expérience utilisateur fluide tout en permettant le financement de l'application</li>
                    <li><strong>Accès complet aux fonctionnalités</strong> : Toutes les fonctionnalités de l'application sont disponibles gratuitement</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Version Premium</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Suppression des publicités</strong> : Profitez d'une expérience sans aucune interruption publicitaire</li>
                    <li><strong>Sauvegarde Google Drive</strong> : Synchronisation automatique dans le cloud</li>
                    <li><strong>Accès à vie</strong> : Un paiement unique, pas d'abonnement récurrent</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Interface et expérience utilisateur */}
        <div 
          ref={(el) => (sectionRefs.current["interface"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("interface")}
          >
            <div className="flex items-center gap-3">
              <FaPalette className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Interface et expérience utilisateur
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "interface" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "interface" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Design moderne</strong> : Interface épurée et intuitive</li>
                <li><strong>Thème adaptatif</strong> : Support du mode clair et sombre</li>
                <li><strong>Navigation fluide</strong> : Parcourez facilement toutes les fonctionnalités</li>
                <li><strong>Recherche rapide</strong> : Trouvez instantanément recettes, produits ou listes</li>
                <li><strong>Filtres avancés</strong> : Filtrez vos recettes par tags, catégories, difficulté, temps de préparation</li>
                <li><strong>Collections</strong> : Organisez vos recettes en collections personnalisées</li>
                <li><strong>Favoris</strong> : Marquez vos recettes préférées pour un accès rapide</li>
              </ul>
            </div>
          )}
        </div>

        {/* Impact environnemental */}
        <div 
          ref={(el) => (sectionRefs.current["impact-env"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("impact-env")}
          >
            <div className="flex items-center gap-3">
              <FaLeaf className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Impact environnemental
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "impact-env" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "impact-env" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                En utilisant Season, vous contribuez à :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Réduire l'empreinte carbone</strong> : En privilégiant les produits locaux de saison, vous réduisez les transports longs et l'énergie nécessaire à la conservation</li>
                <li><strong>Soutenir les producteurs locaux</strong> : Les produits de saison proviennent souvent de sources locales</li>
                <li><strong>Réduire le gaspillage alimentaire</strong> : Une meilleure planification permet de réduire les déchets</li>
                <li><strong>Consommation responsable</strong> : Prise de conscience accrue de la saisonnalité des produits</li>
              </ul>
            </div>
          )}
        </div>

        {/* Spécifications techniques */}
        <div 
          ref={(el) => (sectionRefs.current["specifications"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("specifications")}
          >
            <div className="flex items-center gap-3">
              <FaMobileAlt className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Spécifications techniques
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "specifications" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "specifications" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Plates-formes</strong> : Android</li>
                <li><strong>Langue</strong> : Français (interface et contenu)</li>
                <li><strong>Taille</strong> : Optimisée pour smartphones et tablettes</li>
                <li><strong>Connexion Internet</strong> : Requise pour l'importation de recettes et la synchronisation cloud (optionnelle pour l'utilisation de base)</li>
                <li><strong>Permissions</strong> : Stockage (sauvegarde d'images), Internet (importation)</li>
              </ul>
            </div>
          )}
        </div>

        {/* Cas d'utilisation */}
        <div 
          ref={(el) => (sectionRefs.current["cas-utilisation"] = el)}
          className="border border-gray-700 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-2 md:py-3 text-left bg-neutral-800 hover:bg-neutral-700 transition-colors duration-200 flex justify-between items-center"
            onClick={() => handleToggleSection("cas-utilisation")}
          >
            <div className="flex items-center gap-3">
              <FaUsers className="text-[#7CA834] text-xl" />
              <span className="text-lg font-normal md:font-semibold text-gray-50">
                Cas d'utilisation
              </span>
            </div>
            <FaChevronDown
              className={`text-[#7CA834] transform transition-transform duration-200 ${
                expandedSection === "cas-utilisation" ? "rotate-180" : ""
              }`}
            />
          </button>
          {expandedSection === "cas-utilisation" && (
            <div className="px-4 py-3 bg-neutral-800 text-gray-50">
              <p className="mb-4">
                Season est idéal pour :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Familles</strong> : Planifiez les repas de la semaine et générez automatiquement les listes de courses</li>
                <li><strong>Cuisiniers amateurs</strong> : Découvrez de nouvelles recettes et organisez votre collection</li>
                <li><strong>Personnes soucieuses de l'environnement</strong> : Privilégiez les produits de saison facilement</li>
                <li><strong>Étudiants et jeunes actifs</strong> : Organisez vos repas avec un budget optimisé</li>
                <li><strong>Personnes qui cuisinent régulièrement</strong> : Gérez efficacement votre bibliothèque de recettes</li>
              </ul>
            </div>
          )}
        </div>

        {/* Assets Section - Non-collapsible */}
        <section className="bg-neutral-800 rounded-lg p-4 md:p-6 border border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <FaImage className="text-[#7CA834] text-xl" />
            <h2 className="md:text-2xl text-xl font-semibold text-white">
              Assets (Images)
            </h2>
          </div>
          
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-300">
              Sélectionnez les images pour les télécharger en format compressé (ZIP).
            </p>
            <label className="flex items-center gap-2 cursor-pointer text-gray-50">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleToggleAll}
                className="w-4 h-4 text-[#7CA834] bg-neutral-700 border-gray-600 rounded focus:ring-[#7CA834] focus:ring-2"
              />
              <span className="text-sm font-medium">
                {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
              </span>
            </label>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
            {assets.map((asset) => (
              <div key={asset.id} className="flex flex-col items-center">
                <div className="relative w-full aspect-square mb-2 rounded-lg overflow-hidden border border-gray-700 bg-neutral-700">
                  <img
                    src={asset.src}
                    alt={asset.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedAssets.has(asset.id)}
                    onChange={() => handleAssetToggle(asset.id)}
                    className="w-4 h-4 text-[#7CA834] bg-neutral-700 border-gray-600 rounded focus:ring-[#7CA834] focus:ring-2"
                  />
                  <span className="text-sm">{asset.name}</span>
                </label>
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleDownloadZip}
              disabled={selectedAssets.size === 0}
              className={`px-6 py-2 font-semibold rounded-lg transition-colors duration-200 flex items-center gap-2 ${
                selectedAssets.size > 0
                  ? 'bg-[#7CA834] hover:bg-[#6a9230] text-white cursor-pointer'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              <FaDownload />
              <span>
                {selectedAssets.size > 0
                  ? `Télécharger les images sélectionnées (${selectedAssets.size})`
                  : 'Sélectionnez des images pour télécharger'}
              </span>
            </button>
          </div>
        </section>

        {/* Informations supplémentaires */}
        <section className="bg-neutral-800 rounded-lg p-4 md:p-6 border border-gray-700">
          <h2 className="md:text-2xl text-xl font-semibold text-white mb-4">
            Informations supplémentaires
          </h2>
          <dl className="space-y-3 text-gray-50">
            <div>
              <dt className="font-semibold">Nom de l'application</dt>
              <dd>Season: Listes et Recettes</dd>
            </div>
            <div>
              <dt className="font-semibold">Version</dt>
              <dd>1.3.5</dd>
            </div>
            <div>
              <dt className="font-semibold">Développeur</dt>
              <dd>Kiatoweb</dd>
            </div>
            <div>
              <dt className="font-semibold">Disponibilité</dt>
              <dd>Google Play Store (Android)</dd>
            </div>
            <div>
              <dt className="font-semibold">Site web</dt>
              <dd>season-app.com</dd>
            </div>
            <div>
              <dt className="font-semibold">Lien Google Play</dt>
              <dd>https://play.google.com/store/apps/details?id=com.kiatoweb.season</dd>
            </div>
          </dl>
        </section>

        <footer className="text-center text-gray-400 text-sm italic border-t border-gray-700 pt-6">
          Cette description peut être utilisée pour les communiqués de presse, les fiches produits des stores, les articles de blog, les présentations marketing et toute communication média relative à l'application Season.
        </footer>
      </div>
    </>
  );
}

export default Media;

